import { Fragment } from 'react/jsx-runtime';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Markdown from 'react-markdown';

import { TpDataContext } from '../../../data/interfaces';
import { getDataTable, TpChartConfigParams, TpChartDataBinding } from '../charts/chartDataBindings';
import { TpDocViewerElement, TpChartManifest, TpChartManifestAspectDef } from '../charts/interfaces';
import { TpChartDefinition } from '../dashboard/interfaces';

import { DocViewerElement } from './WidgetDocViewerElements';
import {
  getAspectElements,
  getCompoundAspectElements,
  getConfigParamElements,
  getDataMatrixElements,
  getSecondaryAspectElements,
} from './ManifestElementMappers';
import { checkConfigParamVisibilityConditions } from '../helpers/configParamConditions';

// reuse styles from chart types documentation
import chartDocumentationStyles from '../chart-types-documentation/ChartTypesDocumentation.module.scss';
import docViewerStyles from './WidgetDocViewer.module.scss';

interface TpWidgetDocViewerProps {
  chartTypeManifest: TpChartManifest;
  dataContext: TpDataContext;
  definition: TpChartDefinition;
  currentDataBinding: TpChartDataBinding;
  currentConfigParams: TpChartConfigParams;
  renderUnboundAspects?: boolean;
}

interface TpWidgetDocViewerGroupState extends TpWidgetDocViewerProps {
  groupId: string;
  groupName: string;
}

interface TpDocViewerSectionProps {
  name: string;
  key: string;
  elements: TpDocViewerElement[];
}

function getVisibleAspects(aspectList: TpChartManifestAspectDef[], groupId: string) {
  return aspectList.filter((aspect) => aspect.groupId === groupId).filter((aspect) => !aspect.hidden);
}

function WidgetDocViewerGroup({
  chartTypeManifest,
  dataContext,
  definition,
  currentDataBinding,
  currentConfigParams,
  renderUnboundAspects = false, // decide to show place holders for unbound aspects
  groupId,
  groupName,
}: TpWidgetDocViewerGroupState) {
  const dataTable = getDataTable(dataContext, currentDataBinding.dataTableId);

  let visibleAspects = getVisibleAspects(chartTypeManifest.aspects, groupId);
  let visibleSecondaryAspects = getVisibleAspects(chartTypeManifest.secondTable?.aspects ?? [], groupId);
  let visibleCompoundAspects = getVisibleAspects(chartTypeManifest.compoundAspects ?? [], groupId);
  const documentedConfigParams = chartTypeManifest.configParams.filter(
    (configParam) => configParam.groupId === groupId && configParam.documentation,
  );
  const visibleConfigParams = documentedConfigParams.filter((configParam) =>
    checkConfigParamVisibilityConditions(dataTable, configParam, currentDataBinding, currentConfigParams),
  );

  // Filter out all unbound aspects
  if (!renderUnboundAspects) {
    visibleAspects = visibleAspects.filter((aspect) => currentDataBinding.aspectBindings[aspect.id]);
    visibleSecondaryAspects = visibleSecondaryAspects.filter(
      (aspect) => currentDataBinding.secondAspectBindings?.[aspect.id],
    );
    visibleCompoundAspects = visibleCompoundAspects.filter(
      (aspect) => (currentDataBinding.compoundAspectBindings?.[aspect.id]?.length ?? 0) > 0,
    );
  }

  // Map to documentation elements
  const aspectElements = visibleAspects.map((aspect) => ({
    name: aspect.name,
    key: aspect.id,
    elements: getAspectElements(aspect, dataContext, currentDataBinding, currentConfigParams),
  }));
  const secondaryAspectElements = visibleSecondaryAspects.map((aspect) => ({
    name: aspect.name,
    key: aspect.id,
    elements: getSecondaryAspectElements(aspect, dataContext, currentDataBinding, currentConfigParams),
  }));
  const compoundAspectElements = visibleCompoundAspects.map((aspect) => ({
    name: aspect.name,
    key: aspect.id,
    elements: getCompoundAspectElements(aspect, dataContext, currentDataBinding, currentConfigParams),
  }));
  const configParamElements = visibleConfigParams.map((configParam) => ({
    name: configParam.name,
    key: configParam.id,
    elements: getConfigParamElements(configParam, dataContext, currentDataBinding, currentConfigParams),
  }));

  const allElements = [
    ...aspectElements,
    ...secondaryAspectElements,
    ...compoundAspectElements,
    ...configParamElements,
  ];

  // Handle data matrix property groups
  if (chartTypeManifest.showDataMatrix && chartTypeManifest.dataMatrixDocumentationGroup === groupId) {
    const dataMatrixElements = getDataMatrixElements(dataContext, currentDataBinding);
    if (dataMatrixElements) {
      allElements.unshift({
        name: 'Data',
        key: 'data-matrix-columns',
        elements: dataMatrixElements,
      });
    }
  }

  // There are no visible elements in the group
  if (allElements.length === 0) return null;

  return (
    <Fragment>
      <h2>{groupName}</h2>

      {allElements.map((section: TpDocViewerSectionProps) => (
        <div className={docViewerStyles.section} key={section.key}>
          <h3>{section.name}</h3>
          {section.elements.map((element) => (
            <DocViewerElement key={element.id} {...element} />
          ))}
        </div>
      ))}
    </Fragment>
  );
}

function WidgetDocViewer(props: TpWidgetDocViewerProps) {
  const { chartTypeManifest, currentConfigParams } = props;
  const widgetDescription = currentConfigParams.customDescription;

  return (
    <div className={chartDocumentationStyles.detailedContainer}>
      <h1>
        <FontAwesomeIcon icon={['fad', 'info-circle']} className={docViewerStyles.title} />
      </h1>

      {widgetDescription && <Markdown>{widgetDescription}</Markdown>}

      {chartTypeManifest.groups.map((group) => (
        <WidgetDocViewerGroup key={group.groupId} {...props} {...group} />
      ))}

      {/* Todo: Future Chart usability section */}
    </div>
  );
}

export default WidgetDocViewer;
